import { Link } from 'react-router-dom';
import { Clock, Users, Calendar, ArrowRight, CheckCircle } from 'lucide-react';
import { courses, offerings } from '../data/courses';

const Courses = () => {
  const availableCourses = courses.filter(c => c.status === 'available');
  const comingSoonCourses = courses.filter(c => c.status === 'coming-soon');

  return (
    <div data-testid="courses-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-white via-gray-50 to-brand-turquoise/5">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="badge-lime inline-block mb-6">Learn & Grow</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-blue leading-tight mb-6">
              Courses That Transform Careers
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Expert-led courses designed specifically for STEAM professionals. 
              Learn at your own pace and gain skills that matter.
            </p>
          </div>
        </div>
      </section>

      {/* Available Courses */}
      <section className="section-padding bg-white" data-testid="available-courses-section">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-8">
            Available Now
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {availableCourses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-hover flex flex-col md:flex-row"
                data-testid={`course-card-${course.id}`}
              >
                <div className="md:w-2/5 aspect-video md:aspect-auto overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-3/5 p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="badge-lime text-xs">{course.format}</span>
                    {course.featured && (
                      <span className="text-xs font-medium text-brand-magenta">Featured</span>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-blue mb-2">
                    {course.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 flex-grow">
                    {course.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {course.whoIsItFor.split(' ').slice(0, 3).join(' ')}...
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="font-accent font-bold text-2xl text-brand-blue">
                      {course.price}
                    </span>
                    <button
                      className="btn-primary text-sm py-2.5 px-5"
                      data-testid={`enroll-btn-${course.id}`}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Courses */}
      <section className="section-padding bg-gray-50" data-testid="coming-soon-section">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-8">
            Coming Soon
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {comingSoonCourses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-2xl p-6 border border-gray-100 opacity-90"
                data-testid={`course-card-${course.id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {course.format}
                  </span>
                  <span className="text-xs font-medium text-brand-turquoise">
                    Coming Soon
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-blue mb-2">
                  {course.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {course.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {course.duration}
                  </span>
                </div>
                <button
                  className="btn-outline w-full text-sm"
                  data-testid={`waitlist-btn-${course.id}`}
                >
                  Join Waitlist
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Resources */}
      <section className="section-padding bg-white" data-testid="resources-section">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-8">
            Quick Resources
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offerings.map((offering) => (
              <div
                key={offering.id}
                className="bg-gray-50 rounded-xl p-6 border-l-4 border-brand-lime"
                data-testid={`resource-card-${offering.id}`}
              >
                <span className="text-xs font-accent text-brand-turquoise uppercase tracking-wider">
                  {offering.type}
                </span>
                <h3 className="font-heading font-bold text-lg text-brand-blue mt-2 mb-2">
                  {offering.title}
                </h3>
                <p className="text-gray-600 text-sm mb-2">{offering.description}</p>
                <p className="text-brand-turquoise text-xs font-medium mb-4">{offering.downloadableNote}</p>
                <div className="flex items-center justify-between">
                  <span className="font-accent font-bold text-lg text-brand-blue">
                    {offering.price}
                  </span>
                  <button className="btn-primary text-sm py-2 px-4">
                    Download Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-blue">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            Not sure where to start?
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Book a free consultation and we'll help you find the right path for your career goals.
          </p>
          <Link to="/consulting" className="btn-primary bg-brand-magenta hover:bg-pink-600">
            Book Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Courses;
